import { useEffect, useRef, useState } from "react";
import { ArrowLeft } from "lucide-react";
import type { ProductInfoCreative } from "@/feature/ads/cretives/types/creatives.types";
import { useAuth } from "@/context/AuthContext";
import type { Language } from "@/utils/dictionary";
import { Step1SelectProduct } from "@/feature/ads/cretives/components/modals/steps/Step1SelectProduct";
import { Step2VideoStudio } from "./Step2VideoStudio";
import { useVideoDraftStore } from "@/store/videoDraftStore";
import FluxiLogo from "@/assets/svg/FluxiLogo.svg";
import { Chip } from "@/components/common/chip/Chip";
import { BuyCreditsModal } from "@/components/modals/BuyCreditsModal";
import { ModalWrapper } from "@/components/modals/ModalWrapper";
import { membershipStatsAxios } from "@/services/membership/MembershipAxios";
import { dictionary } from "@/utils/dictionary";

type Step = 1 | 2;

const SHELL_TRANSITION_MS = 240;
const PANEL_TRANSITION_MS = 280;
const PANEL_OPEN_DELAY_MS = 170;
const SHELL_CLOSE_DELAY_MS = 130;

interface CreateVideoModalProps {
  open: boolean;
  onClose: () => void;
  initialProduct?: ProductInfoCreative | null;
}

export function CreateVideoModal({ open, onClose, initialProduct }: CreateVideoModalProps) {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const shellNavRef = useRef<HTMLDivElement | null>(null);
  const shellCreditsRef = useRef<HTMLDivElement | null>(null);
  const confirmRef = useRef<HTMLDivElement | null>(null);
  const { language, credits, setCredits } = useAuth();
  const [step, setStep] = useState<Step>(initialProduct ? 2 : 1);
  const [selectedProduct, setSelectedProduct] = useState<ProductInfoCreative | null>(initialProduct ?? null);
  const [studioStage, setStudioStage] = useState<string>("style");
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [isOpenBuyCreditsModal, setIsOpenBuyCreditsModal] = useState(false);
  const [endMembershipDate, setEndMembershipDate] = useState("");
  const [isRendered, setIsRendered] = useState(open);
  const [shellVisible, setShellVisible] = useState(false);
  const [panelVisible, setPanelVisible] = useState(false);

  // Phase 2 fix: block click-outside while a draft is active. The user
  // should leave the preview through explicit actions so we can clear the
  // draft store/localStorage intentionally.
  const draftStoreStatus = useVideoDraftStore((s) => s.storeStatus);
  const abandonDraft = useVideoDraftStore((s) => s.abandon);
  // Phase 2 V4 (Apr 18 2026) — used to rehydrate a minimal ProductInfoCreative
  // when the user refreshed mid-preview and the modal auto-reopened via
  // AdsPage's resumeFromStorage effect. Step2VideoStudio needs a `product`
  // prop (required) but in draftMode it only renders PreviewApproval,
  // which reads everything from the draft store — so a stub product
  // populated from the draft fields is enough.
  const draftForResume = useVideoDraftStore((s) => s.draft);
  // Only pre-approve draft states should own the modal. Once the user
  // approves, rendering moves to the gallery loading card and should not
  // block creating another video.
  const isDraftActive =
    draftStoreStatus === "directing" ||
    draftStoreStatus === "ready" ||
    draftStoreStatus === "approving" ||
    draftStoreStatus === "rejecting" ||
    draftStoreStatus === "editing_script" ||
    draftStoreStatus === "regenerating_image" ||
    draftStoreStatus === "regenerating_style" ||
    draftStoreStatus === "generating_audio";
  const hasProgress =
    step === 2 ||
    !!selectedProduct ||
    isDraftActive ||
    draftStoreStatus === "error" ||
    draftStoreStatus === "done";

  const closeCleanly = async () => {
    setShowCancelConfirm(false);
    await abandonDraft();
    setStep(initialProduct ? 2 : 1);
    setSelectedProduct(initialProduct ?? null);
    setStudioStage("style");
    onClose();
  };

  const requestClose = () => {
    if (hasProgress) {
      setShowCancelConfirm(true);
      return;
    }
    onClose();
  };

  const handleCreditsRefreshed = (newCredits: number) => {
    setCredits(newCredits);
  };

  useEffect(() => {
    let frame: number | null = null;
    const timers: number[] = [];

    if (open) {
      setIsRendered(true);
      setShellVisible(false);
      setPanelVisible(false);
      frame = window.requestAnimationFrame(() => {
        setShellVisible(true);
        timers.push(window.setTimeout(() => setPanelVisible(true), PANEL_OPEN_DELAY_MS));
      });
    } else {
      setPanelVisible(false);
      timers.push(window.setTimeout(() => setShellVisible(false), SHELL_CLOSE_DELAY_MS));
      timers.push(
        window.setTimeout(
          () => setIsRendered(false),
          SHELL_CLOSE_DELAY_MS + Math.max(SHELL_TRANSITION_MS, PANEL_TRANSITION_MS)
        )
      );
    }

    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [open]);

  // Phase 2 V9 (Apr 19 2026 late night — BUG FIX) — this effect used to have
  // `isDraftActive` and `draftForResume` in its deps. That caused a nasty
  // regression: when the user clicked "Crear mi video", PreviewApproval
  // called `reset()` on the draft store (flipping isDraftActive → false
  // and draftForResume → null) BEFORE `onDone()` propagated `open=false`.
  // Between those two ticks, this effect re-ran with `open=true` +
  // `isDraftActive=false` + `initialProduct=null`, fell through to the
  // ELSE branch, and called `setStep(1) + setSelectedProduct(null)`,
  // yanking the user back to the product picker.
  //
  // Fix: only run on the `open` false→true transition. A ref tracks the
  // previous value of `open` so the resume logic fires exactly once per
  // modal open, and subsequent draft-store changes don't reset the step.
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (!open) {
      prevOpenRef.current = false;
      setShowCancelConfirm(false);
      setIsOpenBuyCreditsModal(false);
      return;
    }
    // Already open — ignore. This is what prevents the reset-triggered
    // step=1 snap back to product picker during the approve flow.
    if (prevOpenRef.current) return;
    prevOpenRef.current = true;

    // Phase 2 V4 — prioritize resuming an active draft over the wizard.
    // If we opened because AdsPage detected a draft in storage, skip
    // straight to step 2 with a synthetic product built from the draft,
    // so the user lands on the preview they already paid for instead
    // of being asked to pick a product again.
    if (isDraftActive && draftForResume) {
      setStep(2);
      setSelectedProduct({
        product_id: draftForResume.product_id ?? "",
        website_id: "",
        name: draftForResume.product_name ?? "",
        description: "",
        image_url: draftForResume.product_image_url ?? undefined,
      });
      return;
    }
    if (initialProduct) {
      setStep(2);
      setSelectedProduct(initialProduct);
    } else {
      setStep(1);
      setSelectedProduct(null);
    }
  }, [open, initialProduct, isDraftActive, draftForResume]);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setLoadingCredits(true);

    membershipStatsAxios()
      .then((res) => {
        if (cancelled) return;
        if (res?.active_membership) {
          setCredits(res.active_membership.credits ?? 0);
          setEndMembershipDate(res.active_membership.ends_at ?? "");
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingCredits(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, setCredits]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (confirmRef.current?.contains(target)) return;
      if (shellNavRef.current?.contains(target)) return;
      if (shellCreditsRef.current?.contains(target)) return;
      if (!modalRef.current?.contains(target)) {
        const isInsideNestedModal =
          target instanceof Element && target.closest("[data-fluxi-nested-modal]");
        if (!isInsideNestedModal) {
          requestClose();
        }
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, requestClose]);

  if (!isRendered) return null;

  // Phase 5.4: cuando el draft está corriendo (director Gemini o regenerate
  // style), achicamos el modal entero para no mostrar una pantalla gigante
  // con un loader chiquito en el medio. Mismo patrón que el onboarding del
  // funnel cuando está procesando.
  const isLoadingDraft =
    step === 2 &&
    (draftStoreStatus === "directing" ||
      draftStoreStatus === "regenerating_style");
  const isAvatarLoadingStage = step === 2 && studioStage === "avatar-loading" && !isLoadingDraft && !isDraftActive;
  const isSharedLoadingStage = isLoadingDraft || isAvatarLoadingStage;
  const isStyleStage = step === 2 && studioStage === "style" && !isSharedLoadingStage && !isDraftActive;
  const isAngleStage = step === 2 && studioStage === "angle" && !isSharedLoadingStage;
  const isAvatarStage = step === 2 && studioStage === "avatar" && !isSharedLoadingStage && !isDraftActive;

  // Estilos inline para tener interpolación suave entre tamaños. Tailwind
  // max-w-* no anima entre clases distintas, así que usamos width directo.
  // IMPORTANTE: ambos states usan height fijo en px — CSS no puede animar
  // entre `auto` y un valor concreto, y eso causaba el "salto" de la
  // transición entre el loader y el preview.
  const sizeStyle: React.CSSProperties =
    step === 2 && !isSharedLoadingStage && isDraftActive
      ? {
          width: "min(95vw, 1024px)",
          height: "min(90vh, 860px)",
        }
      : step === 2 && isSharedLoadingStage
      ? {
          width: "min(88vw, 640px)",
          height: "min(82vh, 540px)",
        }
      : isAngleStage
      ? {
          width: "min(92vw, 780px)",
          height: "min(92vh, 620px)",
        }
      : isStyleStage
      ? {
          width: "min(90vw, 560px)",
          height: "min(86vh, 430px)",
        }
      : isAvatarStage
      ? {
          width: "min(78vw, 820px)",
          height: "min(84vh, 760px)",
        }
      : step === 2 && !isSharedLoadingStage
      ? {
          width: "min(92vw, 860px)",
          height: "min(88vh, 580px)",
        }
      : {
          // Step 1 (selección de producto): height auto, el contenido
          // decide su propio alto.
          width: "min(95vw, 480px)",
          height: "auto",
        };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className={`absolute inset-0 bg-white/90 backdrop-blur-md transition-opacity duration-150 ease-out ${
          shellVisible ? "opacity-100" : "opacity-0"
        }`}
        style={{ transitionDuration: `${SHELL_TRANSITION_MS}ms` }}
      />

      <div
        ref={shellNavRef}
        className={`absolute left-6 top-6 z-20 flex items-center gap-3 transition-[opacity,transform] duration-150 ease-out md:left-8 md:top-7 ${
          shellVisible ? "translate-y-0 opacity-100" : "-translate-y-1 opacity-0"
        }`}
        style={{ transitionDuration: `${SHELL_TRANSITION_MS}ms` }}
      >
        <button
          type="button"
          onClick={requestClose}
          aria-label="Volver"
          className="flex h-8 w-8 items-center justify-center text-slate-700 transition hover:text-fluxi"
        >
          <ArrowLeft className="h-5 w-5" strokeWidth={2} />
        </button>
        <img src={FluxiLogo} alt="Fluxi" className="h-7 w-auto -translate-y-0.5" />
      </div>

      <div
        ref={shellCreditsRef}
        className={`absolute right-5 top-5 z-20 transition-[opacity,transform] duration-150 ease-out md:right-8 md:top-6 ${
          shellVisible ? "translate-y-0 opacity-100" : "-translate-y-1 opacity-0"
        }`}
        style={{ transitionDuration: `${SHELL_TRANSITION_MS}ms` }}
      >
        <Chip
          text={dictionary["credits"]?.[language as Language] ?? "Créditos"}
          value={credits.toString()}
          onClick={() => setIsOpenBuyCreditsModal(true)}
          loading={loadingCredits}
          compact
        />
      </div>

      <div
        ref={modalRef}
        className={`relative transition-[opacity,transform] duration-200 ease-out ${
          panelVisible ? "scale-100 opacity-100" : "scale-[0.985] opacity-0"
        }`}
        style={{ transitionDuration: `${PANEL_TRANSITION_MS}ms` }}
      >
        <div
          className="relative overflow-hidden rounded-2xl border border-border/50 bg-background shadow-2xl transition-[width,height] duration-500 ease-out"
          style={sizeStyle}
        >
          {step === 1 && (
            <Step1SelectProduct
              language={language as Language}
              onClose={onClose}
              titleText="Selecciona un producto"
              hideSubtitle
              headerIcon="package"
              hideFooterCancel
              selectionMode="product"
              onSelectProduct={(product) => {
                setSelectedProduct(product);
                setStep(2);
              }}
            />
          )}
          {step === 2 && selectedProduct && (
            <Step2VideoStudio
              product={selectedProduct}
              onClose={onClose}
              onBackToProduct={() => {
                setSelectedProduct(null);
                setStep(1);
                setStudioStage("style");
              }}
              onStageChange={setStudioStage}
            />
          )}
        </div>
      </div>

      {showCancelConfirm ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/40 backdrop-blur-[2px]">
          <div
            ref={confirmRef}
            className="w-[min(92vw,420px)] rounded-2xl border border-slate-200 bg-white p-5 text-center shadow-2xl"
          >
            <h3 className="text-base font-semibold text-slate-950">
              ¿Cerrar creación de video?
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-500">
              Se perderá el progreso de este flujo y podrás empezar de nuevo limpio.
            </p>
            <div className="mt-5 flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => setShowCancelConfirm(false)}
                className="rounded-xl px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
              >
                Seguir aquí
              </button>
              <button
                type="button"
                onClick={() => void closeCleanly()}
                className="rounded-xl bg-fluxi px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-indigo-200 transition hover:bg-fluxi/90"
              >
                Cerrar y limpiar
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isOpenBuyCreditsModal ? (
        <div data-fluxi-nested-modal>
          <ModalWrapper isOpen={isOpenBuyCreditsModal} onClose={() => setIsOpenBuyCreditsModal(false)}>
            <BuyCreditsModal
              onClose={() => setIsOpenBuyCreditsModal(false)}
              nextRenewalDate={endMembershipDate}
              language={language as Language}
              openInNewTab={true}
              onCreditsRefreshed={handleCreditsRefreshed}
            />
          </ModalWrapper>
        </div>
      ) : null}
    </div>
  );
}
